import { useState } from 'react'
import { Shield, Info, Check, Lock } from 'lucide-react'
import { useI18n } from '../i18n'
import { CLAIM_DISPLAY } from '../utils/claimUrl.js'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * VisitPrepDisclaimer — Agreement gate before showing Visit Prep Document
 * Patient must acknowledge privacy, liability, and sharing best practices.
 */
export default function VisitPrepDisclaimer({ onAgree, onCancel }) {
  const { t } = useI18n()
  // ★★ v242.99.0 (TF b37 #4) — "Lets simplify the share agreement. List each as a
  //   step. Then agree to all box. Acknowledge button."
  //   Was: a master "Agree to all" ABOVE the items, plus a checkbox on each of the
  //   three — four controls for one decision, and the master sat before the text it
  //   agreed to, so the fastest path through was to tick it and never read them.
  //   Now: the three are numbered STEPS you read, then ONE agreement underneath
  //   them, then Acknowledge. Read, then agree, then act — in that order.
  //   ★ Still all three: the single checkbox names them explicitly and nothing is
  //     dropped from the text. What changed is the ORDER and the control count,
  //     not what the patient is agreeing to.
  const [agreed, setAgreed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const items = [
    {
      title: t('visitPrep.privacyDataOwnership'),
      text: t('visitPrep.iUnderstandThisDocumentContains'),
    },
    {
      title: t('visitPrep.platformDisclaimer'),
      text: t('visitPrep.iUnderstandOrellaHealthIs'),
    },
    {
      title: t('visitPrep.secureSharingPractices'),
      text: t('visitPrep.iUnderstandThatSharingThe'),
    },
  ]

  return (
    <div className="space-y-3">
      {/* Agreement card */}
      <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-2.5" style={{ background: '#15354F08' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#E9F8FF' }}>
            <Shield size={18} className="text-[#15354F]" />
          </div>
          <div>
            <p className="text-[13px] font-bold text-[#15354F]">{t('visitPrep.documentSharingAgreement')}</p>
            <p className="text-[10px] text-gray-400">{t('visitPrep.pleaseReviewAndAcknowledgeEach')}</p>
          </div>
        </div>

        {/* ★ THE THREE STEPS — READ-ONLY. They were <button>s with checkboxes;
            they are now numbered rows with no control on them at all.
            ORELLA_RULES §1bh(a): an affordance is what the SHAPE promises. A row
            that cannot be toggled must not look like a row that can, so the
            checkbox and the <button> both go and the number takes their place. */}
        <div className="px-4 py-3 space-y-1">
          {items.map((item, idx) => (
            <div key={idx}>
              {idx > 0 && <div className="border-t border-gray-100" />}
              <div className="flex items-start gap-3 w-full py-2.5">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: '#E9F8FF', color: '#15354F', fontSize: 11, fontWeight: 800 }}
                  aria-hidden="true"
                >
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <p className="text-[12px] font-bold text-[#15354F]">{item.title}</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{item.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ★★ ONE AGREEMENT, AND IT SITS AFTER THE THING IT AGREES TO. */}
        <button
          onClick={() => setAgreed(v => !v)}
          role="checkbox"
          aria-checked={agreed}
          className="w-full px-4 py-3 flex items-center gap-3 border-t border-gray-100 text-left active:bg-gray-50 transition-colors"
          style={{ background: agreed ? '#F0FDF4' : 'white' }}
        >
          <div
            className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all"
            style={{
              borderColor: agreed ? '#66D170' : '#D1D5DB',
              background: agreed ? '#66D170' : 'white',
            }}
          >
            {agreed && <Check size={12} color="white" strokeWidth={3} />}
          </div>
          <p className="text-[12px] font-bold text-[#15354F] tracking-wide">
            {t('visitPrep.agreeToAllItems', 'I have read and agree to all three items above')}
          </p>
        </button>

        {/* Security info tip */}
        <div className="mx-4 mb-3 p-3 rounded-xl" style={{ background: '#E9F8FF' }}>
          <div className="flex items-start gap-2">
            <Info size={14} className="text-[#46A8F9] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-bold text-[#15354F]">{t('visitPrep.howTheShareCodeKeeps')}</p>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                {t('visitPrep.disclaimer', { defaultValue: 'Your unique share code lets your doctor view your case digitally without paper. They enter the code at {{claimHost}} — encrypted, time-limited, and you can revoke access anytime.', claimHost: CLAIM_DISPLAY })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={async () => {
          if (!agreed || isLoading) return
          setIsLoading(true)
          try {
            await onAgree()
          } catch (err) {
            console.error('[VisitPrepDisclaimer] Agree failed:', err)
          } finally {
            setIsLoading(false)
          }
        }}
        disabled={!agreed || isLoading}
        className="w-full py-3.5 rounded-2xl font-bold text-[14px] flex items-center justify-center gap-2 transition-all"
        style={{
          background: agreed ? '#15354F' : '#F3F4F6',
          color: agreed ? 'white' : '#9CA3AF',
          boxShadow: agreed && !isLoading ? '0 8px 20px rgba(21,53,79,0.25)' : 'none',
          opacity: isLoading ? 0.8 : 1,
        }}
      >
        {/* v242.99.0 — "Acknowledge button". The disabled copy used to be
            "Acknowledge all items to continue", which described the four controls
            that no longer exist. These three were hardcoded English; they are keyed
            now, with the English as the default. */}
        {isLoading ? (
          <><OrellaSpinner size={16} tone="inherit" /> {t('visitPrep.preparingDocument', 'Preparing your document…')}</>
        ) : agreed ? (
          <><Check size={16} strokeWidth={2.5} /> {t('visitPrep.acknowledge', 'Acknowledge')}</>
        ) : (
          t('visitPrep.agreeToContinue', 'Agree above to continue')
        )}
      </button>

      <p className="text-center text-[9px] text-gray-300">
        {t('visitPrep.byContinuingLead')}{' '}
        <span className="underline text-[#46A8F9]">{t('visitPrep.privacyPolicy')}</span> {t('visitPrep.and')}{' '}
        <span className="underline text-[#46A8F9]">{t('visitPrep.termsOfService')}</span>
        {/* s102 (Dr. Rami list 3, B5): es reads "... y los Términos de Servicio de Orella."; en tail is empty. */}
        {t('visitPrep.byContinuingTail') ? ` ${t('visitPrep.byContinuingTail')}` : ''}
      </p>
    </div>
  )
}
